(() => {
  // -- Constants
  // Captured uses transition: .6s ease on every card, with no stagger.
  const motionDuration = 600;
  const motionEasing = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
  const stack = [
    { x:27.72, y:-68.58, mobileX:4.14, mobileY:-49.76, angle:-2, opacity:1, layer:4 },
    { x:-94.65, y:30.09, mobileX:-49.76, mobileY:-7.84, angle:8, opacity:.55, layer:1 },
    { x:-54.3, y:-29.09, mobileX:-24.65, mobileY:-29.04, angle:4, opacity:.75, layer:3 },
    { x:74.22, y:-56.95, mobileX:27, mobileY:-46.79, angle:-6, opacity:.75, layer:2 }
  ];

  class MomaxGallery extends HTMLElement {
    // -- State and variables
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.phone = matchMedia('(max-width:740px)');
      this.reduced = matchMedia('(prefers-reduced-motion:reduce)');
      this.stage = this.querySelector('.momax-gallery__stage');
      this.viewport = this.querySelector('.momax-gallery__viewport');
      this.cards = [...this.querySelectorAll('[data-gallery-card]')];
      this.pages = [...this.querySelectorAll('[data-gallery-page]')];
      this.animations = [];
      this.progress = 0;
      this.target = 0;
      this.motion = null;
      this.forced = false;
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      };
      this.resize = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.measure());
      };

      // -- Listeners
      const options = { signal:this.abort.signal };
      window.addEventListener('scroll', () => {
        if (this.stage.getBoundingClientRect().top >= innerHeight * .65) this.forced = false;
        this.schedule();
      }, { ...options, passive:true });
      window.addEventListener('resize', this.resize, options);
      this.phone.addEventListener('change', () => { this.mountVideos(); this.measure(); }, options);
      this.reduced.addEventListener('change', () => { this.forced = false; if (this.reduced.matches) this.querySelectorAll('video').forEach(video => video.pause()); this.measure(); }, options);
      document.addEventListener('visibilitychange', this.schedule, options);
      this.viewport.addEventListener('scroll', () => this.updatePagination(), { ...options, passive:true });
      this.viewport.addEventListener('keydown', event => {
        if (!this.phone.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.open();
        this.viewport.scrollBy({ left:event.key === 'ArrowRight' ? 296 : -296, behavior:this.reduced.matches ? 'instant' : 'smooth' });
      }, options);
      this.addEventListener('focusin', event => { if (event.target.matches(':focus-visible')) this.open(); }, options);
      this.addEventListener('click', event => this.onClick(event), options);
      this.cards.forEach(card => {
        card.addEventListener('mouseenter', () => {
          card.setAttribute('data-hovered', '');
          this.syncCardPlayback(card);
        }, options);
        card.addEventListener('mouseleave', () => {
          card.removeAttribute('data-hovered');
          this.syncCardPlayback(card);
        }, options);
      });
      document.addEventListener('shopify:block:select', event => {
        const card = this.cards.find(item => item.dataset.blockId === event.detail?.blockId);
        if (!card) return;
        this.open();
        if (this.phone.matches) this.viewport.scrollTo({ left:card.offsetLeft - 20 });
      }, options);
      this.mountVideos();
      this.measure();
      this.observer = new ResizeObserver(this.resize);
      this.observer.observe(this.querySelector('.momax-gallery__heading'));
    }

    // -- Functions
    mountVideos() {
      this.cards.forEach(card => {
        const host = card.querySelector('[data-gallery-video]');
        host.querySelector('video')?.pause();
        host.replaceChildren();
        delete card.dataset.playing;
        delete card.dataset.played;
        const template = card.querySelector(this.phone.matches ? '[data-gallery-mobile]' : '[data-gallery-desktop]');
        const button = card.querySelector('.momax-gallery__play');
        const link = card.querySelector('.momax-gallery__link');
        if (button) button.hidden = !template;
        if (link) link.hidden = !!template;
        if (!template) return;
        host.append(template.content.cloneNode(true));
        const video = host.querySelector('video');
        video.muted = true;
        video.playsInline = true;
        const sync = () => {
          const playing = !video.paused && !video.ended;
          card.toggleAttribute('data-playing', playing);
          if (playing) card.setAttribute('data-played', '');
        };
        ['playing', 'pause', 'ended'].forEach(name => video.addEventListener(name, sync, { signal:this.abort.signal }));
        video.addEventListener('error', () => { video.pause(); sync(); }, { signal:this.abort.signal });
        sync();
      });
    }

    measure() {
      if (!this.isConnected) return;
      this.animations.forEach(animation => animation.cancel());
      this.animations = [];
      this.removeAttribute('data-animating');
      this.stage.style.minHeight = '';
      this.viewport.scrollLeft = 0;
      const mobile = this.phone.matches;
      const stage = this.stage.getBoundingClientRect();
      const scale = mobile ? Math.min(1, stage.width / 390) : Math.min(1, stage.width / 1920);
      this.stageHeights = [mobile ? 430 : 1000 * scale, stage.height];
      const rectangles = this.cards.map(card => card.getBoundingClientRect());
      this.cards.forEach((card, index) => {
        const rect = rectangles[index];
        const pose = stack[index % stack.length];
        const width = (mobile ? 210 : 480) * scale;
        const height = (mobile ? 298 : 680) * scale;
        const x = stage.width / 2 + (mobile ? pose.mobileX : pose.x * scale) - width / 2;
        const y = (mobile ? 215 : 500 * scale) + (mobile ? pose.mobileY : pose.y * scale) - height / 2;
        card.style.zIndex = pose.layer;
        const animation = card.animate([
          { transform:`translate(${x - (rect.left-stage.left)}px, ${y - (rect.top-stage.top)}px) rotate(${pose.angle}deg)`, width:`${width}px`, height:`${height}px`, opacity:pose.opacity, borderRadius:mobile?'8px':'18px', boxShadow:'0 14px 32px rgba(0,0,0,.12)' },
          { transform:'translate(0,0) rotate(0deg)', width:`${rect.width}px`, height:`${rect.height}px`, opacity:1, borderRadius:'4px', boxShadow:'0 1px 2px #0000000a,0 4px 8px #00000014' }
        ], { duration:1000, fill:'both' });
        animation.pause();
        this.animations.push(animation);
      });
      this.renderProgress(this.progress);
      this.update();
    }

    update() {
      const rect = this.stage.getBoundingClientRect();
      if (rect.top > innerHeight || rect.bottom < 0) this.forced = false;
      // Separate thresholds avoid repeated reversals when scrolling near the trigger.
      let target = this.target;
      if (rect.top <= innerHeight * .55) target = 1;
      else if (rect.top >= innerHeight * .65) target = 0;
      if (this.reduced.matches || this.forced) {
        this.target = 1;
        this.motion?.timing.cancel();
        this.motion = null;
        this.renderProgress(1);
      } else {
        if (target !== this.target) {
          this.target = target;
          this.motion?.timing.cancel();
          // Let the browser evaluate the reference CSS easing rather than approximating it.
          const timing = new Animation(new KeyframeEffect(null, [], {
            duration:motionDuration * Math.abs(target - this.progress), easing:motionEasing, fill:'both'
          }), document.timeline);
          timing.play();
          this.motion = { from:this.progress, to:target, timing };
        }
        if (this.motion) {
          const { from, to, timing } = this.motion;
          const finished = timing.playState === 'finished';
          const eased = timing.effect.getComputedTiming().progress ?? 0;
          this.renderProgress(finished ? to : from + (to - from) * eased);
          if (finished) { timing.cancel(); this.motion = null; }
          else this.schedule();
        }
      }
      this.cards.forEach(card => {
        if (card.matches(':hover')) card.setAttribute('data-hovered', '');
        if (this.progress === 0) card.removeAttribute('data-played');
        this.syncCardPlayback(card, rect);
      });
    }

    syncCardPlayback(card, stageRect = this.stage.getBoundingClientRect()) {
      const video = card.querySelector('video');
      if (!video) return;
      const expanded = this.progress === 1;
      const visible = stageRect.top < innerHeight && stageRect.bottom > 0 && !document.hidden;
      const bounds = card.getBoundingClientRect();
      const inView = visible && bounds.right > 0 && bounds.left < innerWidth && bounds.bottom > 0 && bounds.top < innerHeight;
      const hovered = card.hasAttribute('data-hovered');
      if (!expanded || !inView || !hovered || this.reduced.matches) {
        video.pause();
      } else if (video.paused && !card.hasAttribute('data-play-pending')) {
        this.play(card, video);
      }
    }

    renderProgress(progress) {
      const expanded = progress === 1;
      if (!expanded && this.progress === 1) this.viewport.scrollLeft = 0;
      this.animations.forEach(animation => { animation.currentTime = progress * 1000; });
      this.stage.style.minHeight = `${this.stageHeights[0] + (this.stageHeights[1] - this.stageHeights[0]) * progress}px`;
      this.cards.forEach(card => { card.querySelector('.momax-gallery__copy').style.opacity = Math.max(0, (progress - .65) / .35); });
      this.querySelector('.momax-gallery__pagination').style.visibility = expanded ? '' : 'hidden';
      this.toggleAttribute('data-expanded', expanded);
      this.toggleAttribute('data-animating', !expanded);
      this.progress = progress;
    }

    play(card, video) {
      card.setAttribute('data-play-pending', '');
      video.play()?.then(() => {
        if (this.progress !== 1 || !card.hasAttribute('data-hovered') || this.reduced.matches || document.hidden) {
          video.pause();
        }
      }).catch(() => {}).finally(() => card.removeAttribute('data-play-pending'));
    }

    open() {
      this.forced = true;
      this.update();
    }

    onClick(event) {
      const page = event.target.closest('[data-gallery-page]');
      if (page) {
        this.open();
        const card = this.cards[Number(page.dataset.galleryPage)];
        this.viewport.scrollTo({ left:card.offsetLeft - 20, behavior:this.reduced.matches ? 'instant' : 'smooth' });
        return;
      }
      const card = event.target.closest('[data-gallery-card]');
      if (!card) return;
      if (this.progress < .9999) { event.preventDefault(); this.open(); return; }
      if (!event.target.closest('.momax-gallery__play')) return;
      const video = card.querySelector('video');
      if (!video) return;
      if (video.paused) {
        card.setAttribute('data-hovered', '');
        this.play(card, video);
      } else {
        card.removeAttribute('data-hovered');
        video.pause();
      }
    }

    updatePagination() {
      const index = Math.min(this.cards.length - 1, Math.round(this.viewport.scrollLeft / 296));
      this.pages.forEach((page, i) => { if (i === index) page.setAttribute('aria-current', 'true'); else page.removeAttribute('aria-current'); });
      this.schedule();
    }

    // -- Lifecycle hooks
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.resizeFrame);
      this.frame = 0;
      this.motion?.timing.cancel();
      this.motion = null;
      this.animations?.forEach(animation => animation.cancel());
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  }
  if (!customElements.get('momax-gallery')) customElements.define('momax-gallery', MomaxGallery);
})();
